import { RegisteredStarComponent } from '../../components/registered-star/registered-star.component';
import { Component, ChangeDetectionStrategy, inject, computed, output, signal, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RaceService } from '../../services/race.service';
import { ResultService } from '../../services/result.service';
import { HumanService } from '../../services/human.service';
import { TranslationService } from '../../services/translation.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { DisplayValuePipe } from '../../pipes/display-value.pipe';
import { DistancePipe } from '../../pipes/distance.pipe';
import { Human, Event as RaceEvent } from '../../models/data.models';
import { RankingSummaryRow, RankingsFacetRow } from '../../services/result.service';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { RESULT_DISCIPLINES, normalizeResultDiscipline } from '../../models/discipline.models';
import { BrandedLoaderComponent } from '../../components/branded-loader/branded-loader.component';
import { StyledSelectComponent } from '../../components/styled-select/styled-select.component';
import { normalizeBreedName } from '../../models/dog-breed.models';
import { currentSeasonStartYear, seasonLabel, seasonStartDate, seasonStartYear } from '../../utils/season';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { includesEveryNameToken } from '../../models/name-search';

export interface RankingData {
  human: Human;
  medals: { first: number; second: number; third: number; fourth: number };
  raceCount: number;
  totalDistanceKm: number;
  totalTimeSeconds: number;
  trend?: 'up' | 'down' | 'stable';
}

export function sortRankings(data: RankingData[]): RankingData[] {
  return [...data].sort((a, b) => {
    const byRaces = b.raceCount - a.raceCount;
    const byDistance = b.totalDistanceKm - a.totalDistanceKm;
    const byTime = a.totalTimeSeconds - b.totalTimeSeconds;
    const byName = a.human.name.localeCompare(b.human.name);
    return byRaces || byDistance || byTime || byName;
  });
}

@Component({
  selector: 'app-rankings',
  standalone: true,
  imports: [RegisteredStarComponent, CommonModule, TranslatePipe, DisplayValuePipe, DistancePipe, RouterModule, BrandedLoaderComponent, StyledSelectComponent, PageHeaderComponent],
  templateUrl: './rankings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RankingsComponent {
  raceService = inject(RaceService);
  resultService = inject(ResultService);
  humanService = inject(HumanService);
  translationService = inject(TranslationService);


  // Pagination state
  itemsPerPage = 20;
  currentPage = signal(1);

  // Filter state
  route = inject(ActivatedRoute);
  searchTerm = signal('');
  regionFilter = signal<string | null>('HAUTS-DE-FRANCE');
  disciplineFilter = signal<string | null>(null);
  rankingScope = signal<'season' | 'all'>('season');
  selectedSeasonStart = signal(currentSeasonStartYear());
  categoryFilter = signal<string | null>(null);
  genderFilter = signal<string | null>(null);
  clubFilter = signal<string>('ALL'); // 'ALL', '_INDEPENDENT_', or specific club name

  // Data state
  isLoading = signal(true);
  rawEvents = signal<RaceEvent[]>([]);
  rankingRows = signal<RankingSummaryRow[]>([]);
  rankingFacets = signal<RankingsFacetRow[]>([]);
  rawResultCount = signal(0);
  private hasLoadedData = signal(false);

  // Reset to page 1 when any filter or sorting changes
  resetPage = effect(() => {
    this.searchTerm();
    this.regionFilter();
    this.disciplineFilter();
    this.rankingScope();
    this.categoryFilter();
    this.genderFilter();
    this.clubFilter();
    untracked(() => this.currentPage.set(1));
  });

  // Reload results when region, discipline, gender or category changes
  private reloadOnFilterChange = effect(() => {
    this.regionFilter();
    this.disciplineFilter();
    this.genderFilter();
    this.categoryFilter();
    untracked(() => {
      if (this.hasLoadedData()) {
        this.reloadResults();
      }
    });
  });

  getFlagEmoji = this.humanService.getFlagEmoji;

  private disciplineLabels: Record<string, string> = {
    'Cani-Trail': 'Cani Trail',
    'Canicross': 'Canicross',
    'CanicrossLong': 'Canicross Long',
    'CanicrossCourt': 'Canicross Court',
    'CanicrossCadet': 'Canicross Cadet',
    'CanicrossMinime': 'Canicross Minime',
    'CanicrossBenjamin': 'Canicross Benjamin',
    'Cani-VTT': 'Cani-VTT',
    'Cani-Trottinette': 'Cani-Trottinette',
    'Cani-Marche': 'Cani-Marche',
    'CANICROSSLONG': 'Canicross Long',
    'CANICROSSCOURT': 'Canicross Court',
    'CANICROSSCADET': 'Canicross Cadet',
    'CANICROSSMINIME': 'Canicross Minime',
    'CANICROSSBENJAMIN': 'Canicross Benjamin',
    'CANI-VTT': 'Cani-VTT',
    'CANI-TROTTINETTE': 'Cani-Trottinette',
    'CANI-MARCHE': 'Cani-Marche',
    'CANICROSS': 'Canicross',
  };

  formatDiscipline(value: string): string {
    const normalized = normalizeResultDiscipline(value);
    const definition = RESULT_DISCIPLINES.find(discipline => discipline.code === normalized);
    return definition?.label ?? this.disciplineLabels[value] ?? value;
  }

  formatRegion(value: string): string {
    if (!value) return value;
    // "HAUTS-DE-FRANCE" → "Hauts-de-France"
    return value
      .split('-')
      .map(part => {
        const lower = part.toLowerCase();
        // Keep short connectors lowercase
        if (['de', 'du', 'd', 'la', 'le', 'les', 'et'].includes(lower)) return lower;
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join('-');
  }

  constructor() {
    // Read the initial URL filters before issuing the first rankings request.
    this.route.queryParams.subscribe(params => {
      this.clubFilter.set(params['club'] ?? 'ALL');
      // Public rankings are intentionally limited to the region currently being validated.
      this.regionFilter.set('HAUTS-DE-FRANCE');
      this.disciplineFilter.set(params['discipline'] !== undefined ? params['discipline'] : null);
      this.categoryFilter.set(params['category'] ?? null);
      this.genderFilter.set(params['gender'] ?? null);
    });

    this.loadData();
  }

  router = inject(Router);

  async loadData() {
    this.isLoading.set(true);
    try {
      const eventsPromise = this.raceService.getPublishedEvents({ includeRaces: true });
      const rankingPromise = this.loadRankingData();
      const [events] = await Promise.all([eventsPromise, rankingPromise]);
      this.rawEvents.set(events);
      this.hasLoadedData.set(true);
    } catch (error) {
      console.error("Error loading rankings data", error);
    } finally {
      this.isLoading.set(false);
    }
  }

  private currentSeasonStartDate(): string {
    return seasonStartDate(this.selectedSeasonStart());
  }

  availableSeasons = computed(() => {
    const currentSeason = currentSeasonStartYear();
    const seasons = new Set<number>([currentSeason, this.selectedSeasonStart()]);
    this.rawEvents().forEach(event => {
      const season = seasonStartYear(event.date);
      if (season !== null) seasons.add(season);
    });
    return Array.from(seasons).sort((a, b) => b - a);
  });

  selectedSeasonLabel = computed(() => seasonLabel(this.selectedSeasonStart()));

  seasonOptions = computed(() => this.availableSeasons().map(year => ({
    value: String(year),
    label: seasonLabel(year),
  })));

  private rankingParams() {
    return {
      region: this.regionFilter(),
      discipline: this.disciplineFilter(),
      gender: this.genderFilter(),
      category: this.categoryFilter(),
      seasonStart: this.rankingScope() === 'season' ? this.currentSeasonStartDate() : null,
    };
  }

  private async loadRankingData(): Promise<number> {
    const params = this.rankingParams();
    const [rows, facets] = await Promise.all([
      this.resultService.getRankingsSummary(params),
      this.resultService.getRankingsFacets(params),
    ]);
    // Filters remain interactive during the initial request. Discard its response
    // if the selection changed while it was loading, and fetch the current view.
    if (JSON.stringify(params) !== JSON.stringify(this.rankingParams())) {
      return this.loadRankingData();
    }
    this.rankingRows.set(rows);
    this.rankingFacets.set(facets);
    const resultCount = facets[0]?.total_results != null
      ? Number(facets[0].total_results)
      : rows.length > 0 ? rows.length : 0;
    this.rawResultCount.set(resultCount);
    return resultCount;
  }
  async reloadResults() {
    this.isLoading.set(true);
    try {
      await this.loadRankingData();
    } catch (error) {
      console.error("Error reloading results", error);
    } finally {
      this.isLoading.set(false);
    }
  }

  onSearch(event: Event) {
    this.searchTerm.set((event.target as HTMLInputElement).value);
  }

  setRegion(_region: string | null) {
    this.router.navigate([], {
      queryParams: { region: 'HAUTS-DE-FRANCE' },
      queryParamsHandling: 'merge',
    });
  }

  setDiscipline(discipline: string | null) {
    this.router.navigate([], { queryParams: { discipline }, queryParamsHandling: 'merge' });
  }

  setRankingScope(scope: 'season' | 'all'): void {
    this.rankingScope.set(scope);
    this.currentPage.set(1);
    if (this.hasLoadedData()) this.reloadResults();
  }


  setSeasonStart(value: string): void {
    const seasonStart = Number(value);
    if (!Number.isInteger(seasonStart)) return;
    this.selectedSeasonStart.set(seasonStart);
    this.rankingScope.set('season');
    this.currentPage.set(1);
    if (this.hasLoadedData()) this.reloadResults();
  }

  setCategory(category: string | null) {
    this.router.navigate([], { queryParams: { category }, queryParamsHandling: 'merge' });
  }

  setGender(gender: string | null) {
    this.router.navigate([], { queryParams: { gender }, queryParamsHandling: 'merge' });
  }

  clearFilters() {
    this.searchTerm.set('');
    this.rankingScope.set('season');
    this.router.navigate([], {
      queryParams: { region: 'HAUTS-DE-FRANCE', discipline: null, category: null, gender: null, club: 'ALL' }
    });
  }

  availableRegions = computed(() => {
    const regions = new Set<string>();
    this.rawEvents().forEach(e => { if (e.region) regions.add(e.region); });
    return Array.from(regions).sort();
  });

  availableDisciplines = computed(() => {
    const discs = new Set<string>();
    this.rawEvents().forEach(e => {
      (e.races || []).forEach(r => {
        const discipline = normalizeResultDiscipline(r.discipline, r.name);
        if (discipline) discs.add(discipline);
      });
    });
    return RESULT_DISCIPLINES.filter(discipline => discs.has(discipline.code)).map(
      discipline => discipline.code,
    );
  });

  currentSeasonLabel = computed(() => seasonLabel(currentSeasonStartYear()));

  formatTotalTime(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  rankings = computed<RankingData[]>(() => {
    const search = this.searchTerm().toLowerCase();
    const clubF = this.clubFilter();
    let data: RankingData[] = this.rankingRows().map(row => ({
      human: {
        id: row.human_id,
        name: row.human_name,
        age: row.human_age,
        gender: row.human_gender as 'Male' | 'Female' | 'Other',
        club: row.human_club,
        nationality: row.human_nationality as 'FRA' | 'BEL' | 'ENG' | undefined,
        imageUrl: row.human_image_url,
      },
      medals: {
        first: Number(row.medals_first), second: Number(row.medals_second),
        third: Number(row.medals_third), fourth: Number(row.medals_fourth),
      },
      raceCount: Number(row.race_count),
      totalDistanceKm: Number(row.total_distance_km),
      totalTimeSeconds: Number(row.total_time_seconds),
      trend: 'stable' as const,
    }));

    // 4. Client-side filters (search + club)
    if (search) {
      data = data.filter(item =>
        includesEveryNameToken(item.human.name, search) ||
        (item.human.club && item.human.club.toLowerCase().includes(search))
      );
    }

    if (clubF !== 'ALL') {
      data = data.filter(item => {
        const club = item.human.club || '';
        if (clubF === '_INDEPENDENT_') return !club;
        return club.toLowerCase() === clubF.toLowerCase();
      });
    }

    return sortRankings(data);
  });

  stats = computed(() => {
    const facets = this.rankingFacets();
    const rankings = this.rankings();

    // 1. Top 3 Clubs (from filtered rankings)
    const clubMap = new Map<string, number>();
    rankings.forEach(r => {
      if (r.human.club) clubMap.set(r.human.club, (clubMap.get(r.human.club) || 0) + 1);
    });
    const topClubs = Array.from(clubMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    // 2. Top 3 Breeds (already aggregated by Supabase)
    const normalizedBreedCounts = new Map<string, number>();
    facets.filter(row => row.facet_type === 'breed' && row.facet_name).forEach(row => {
      const name = normalizeBreedName(row.facet_name);
      normalizedBreedCounts.set(name, (normalizedBreedCounts.get(name) || 0) + Number(row.facet_count));
    });
    const topBreeds = [...normalizedBreedCounts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    // 3. Disciplines stats (already aggregated by Supabase)
    const disciplineStats = facets
      .filter(row => row.facet_type === 'discipline' && row.facet_name)
      .map(row => ({ name: row.facet_name, count: Number(row.facet_count) }))
      .sort((a, b) => b.count - a.count);

    return {
      totalParticipants: rankings.length,
      topClubs,
      topBreeds,
      disciplineStats
    };
  });

  totalPages = computed(() => {
    return Math.ceil(this.rankings().length / this.itemsPerPage);
  });

  paginatedRankings = computed(() => {
    const allRankings = this.rankings();
    const page = this.currentPage();
    const start = (page - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return allRankings.slice(start, end);
  });

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
    }
  }

  nextPage() {
    this.goToPage(this.currentPage() + 1);
  }

  previousPage() {
    this.goToPage(this.currentPage() - 1);
  }
}
